import { DragEvent, ReactNode, RefObject, useCallback, useLayoutEffect } from 'react'
import {
  useFloating,
  useInteractions,
  useRole,
  useDismiss,
  FloatingOverlay,
  FloatingFocusManager,
  useId,
  FloatingTree,
  FloatingNode,
  useFloatingNodeId,
  useFloatingParentNodeId
} from '@floating-ui/react'

interface ModalProps {
  children: ReactNode
  isOpen: boolean
  onClose?: () => void
  className?: string
  initialFocus?: RefObject<HTMLElement | null>
}

export function Modal(props: ModalProps) {
  const parentId = useFloatingParentNodeId()
  if (parentId === null) {
    return (
      <FloatingTree>
        <ModalContent {...props} />
      </FloatingTree>
    )
  }
  return <ModalContent {...props} />
}

function ModalContent({ isOpen, children, onClose, className, initialFocus }: ModalProps) {
  const nodeId = useFloatingNodeId()
  // Prevent file drag/drop events from elements underneath
  const preventDrag = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  // Set up floating UI
  const { refs, context } = useFloating({
    nodeId,
    open: isOpen,
    onOpenChange: (open) => {
      if (!open && onClose) onClose()
    }
  })

  // Remember this modal's opener independently of nested focus managers. Their
  // focus history may otherwise return the parent to an element it is removing.
  useLayoutEffect(() => {
    if (isOpen) refs.setReference(document.activeElement)
  }, [isOpen, refs])

  const dismiss = useDismiss(context, {
    outsidePressEvent: 'pointerdown',
    bubbles: { outsidePress: false, escapeKey: false }
  })
  const role = useRole(context, { role: 'dialog' })

  const { getFloatingProps } = useInteractions([dismiss, role])

  const labelId = useId()
  const descriptionId = useId()

  if (!isOpen) {
    return null
  }

  return (
    <FloatingNode id={nodeId}>
      <FloatingOverlay
        lockScroll
        className={`fixed inset-0 z-10 bg-stone-500 bg-opacity-75 ${className}`}
      >
        <div
          className="h-svh w-svw"
          onDragEnter={preventDrag}
          onDragLeave={preventDrag}
          onDragOver={preventDrag}
          onDrop={preventDrag}
        >
          <FloatingFocusManager context={context} initialFocus={initialFocus}>
            <div
              ref={refs.setFloating}
              aria-labelledby={labelId}
              aria-describedby={descriptionId}
              {...getFloatingProps({
                className: 'flex h-svh w-svw flex-col items-center justify-center'
              })}
            >
              {children}
            </div>
          </FloatingFocusManager>
        </div>
      </FloatingOverlay>
    </FloatingNode>
  )
}
